/**
 * Releve Brand Color Palette
 *
 * This file exports the complete Releve color palette for programmatic use
 * in charts, dynamic styling, and other TypeScript/JavaScript contexts.
 *
 * For Tailwind classes, use: bg-releve-teal, text-releve-coral, etc.
 * For CSS variables, use: var(--releve-teal), var(--releve-coral), etc.
 */

// Complete Releve color palette with hex values
export const releveColors = {
  black: '#1A1A1A',

  teal: {
    dark: '#1B6B6B',
    DEFAULT: '#3A9D9D',
    light: '#6CBFBF',
    pale: '#E5F4F4',
  },

  purple: {
    dark: '#2a1e42',
    DEFAULT: '#3d2c5e',
    light: '#5a3d7e',
    pale: '#f3eef8',
  },

  green: {
    forest: '#2D5A3D',
    DEFAULT: '#5A8C4A',
    lime: '#8CB82B',
    sage: '#A8C686',
  },

  pink: {
    magenta: '#D64B8A',
    dusty: '#C98B98',
    pale: '#E8C8D0',
  },

  maroon: '#8B3A4C',
  coral: '#E87D5E',
  gold: '#C9A84B',

  gray: {
    light: '#E8E8E8',
  },
} as const;

// Flat list of all colors for easy iteration
export const releveColorList = [
  { name: 'black', hex: '#1A1A1A' },
  { name: 'teal-dark', hex: '#1B6B6B' },
  { name: 'teal', hex: '#3A9D9D' },
  { name: 'teal-light', hex: '#6CBFBF' },
  { name: 'teal-pale', hex: '#E5F4F4' },
  { name: 'purple-dark', hex: '#2a1e42' },
  { name: 'purple', hex: '#3d2c5e' },
  { name: 'purple-light', hex: '#5a3d7e' },
  { name: 'purple-pale', hex: '#f3eef8' },
  { name: 'green-forest', hex: '#2D5A3D' },
  { name: 'green', hex: '#5A8C4A' },
  { name: 'green-lime', hex: '#8CB82B' },
  { name: 'green-sage', hex: '#A8C686' },
  { name: 'pink-magenta', hex: '#D64B8A' },
  { name: 'pink-dusty', hex: '#C98B98' },
  { name: 'pink-pale', hex: '#E8C8D0' },
  { name: 'maroon', hex: '#8B3A4C' },
  { name: 'coral', hex: '#E87D5E' },
  { name: 'gold', hex: '#C9A84B' },
  { name: 'gray-light', hex: '#E8E8E8' },
] as const;

/**
 * Chart color palette - ordered for visual distinction
 * Use this array for pie charts, bar charts, line charts, etc.
 * Colors are ordered to maximize contrast between adjacent items.
 */
export const chartColors = [
  '#1B6B6B', // dark teal
  '#E87D5E', // coral
  '#5A8C4A', // green
  '#D64B8A', // magenta
  '#C9A84B', // gold
  '#3A9D9D', // medium teal
  '#8B3A4C', // maroon
  '#8CB82B', // lime
  '#C98B98', // dusty rose
  '#6CBFBF', // light teal
  '#2D5A3D', // forest green
  '#A8C686', // sage
] as const;

/**
 * Category label colors - for tagging and categorization
 * Maps category types to their brand colors
 */
export const categoryColors = {
  // Account categories
  assets: '#1B6B6B',      // dark teal
  liabilities: '#8B3A4C', // maroon
  equity: '#5A8C4A',      // green
  revenue: '#3A9D9D',     // medium teal
  expenses: '#E87D5E',    // coral

  // Project/status categories
  active: '#3A9D9D',      // medium teal
  completed: '#5A8C4A',   // green
  pending: '#C9A84B',     // gold
  cancelled: '#8B3A4C',   // maroon

  // Priority levels
  high: '#E87D5E',        // coral
  medium: '#C9A84B',      // gold
  low: '#3A9D9D',         // medium teal
} as const;

/**
 * Semantic colors mapped to brand palette
 */
export const semanticColors = {
  primary: '#1B6B6B',     // dark teal
  success: '#3A9D9D',     // medium teal
  warning: '#E87D5E',     // coral
  error: '#8B3A4C',       // maroon
  info: '#6CBFBF',        // light teal
} as const;

// Type exports for TypeScript usage
export type ReleveColor = typeof releveColors;
export type ChartColor = typeof chartColors[number];
export type CategoryColorKey = keyof typeof categoryColors;
